import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  getCurrentSiteEnergyConsumption,
  getSiteDABid,
  getTableData,
  getTableNBPT,
  updateSiteDABid,
} from '@/services/eos'
import { getTableMonthlySummary, updateSitePowerPosition } from '@/services/eos/dashboard.service'
import type { ISitePowerPositionForm } from '@/types/eos/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { DateTime } from 'luxon'
import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue'

export const useEOSTable = () => {
  const queryClient = useQueryClient()

  const eosTableQuery = (
    selectedSite: Ref<string | undefined>,
    startHour: Ref<string | undefined>,
    forceRefresh: MaybeRefOrGetter<boolean> = false
  ) =>
    useQuery({
      queryKey: ['eos', 'table', selectedSite, startHour],
      queryFn: () => getTableData(selectedSite.value!, startHour.value, toValue(forceRefresh)),
      enabled: computed(() => !!selectedSite.value && !!startHour.value),
      placeholderData: (prev) => prev,
    })

  const eosTableMonthlySummaryQuery = (
    selectedSite: Ref<string | undefined>,
    forDate: Ref<DateTime | undefined>,
    timezone: Ref<string | undefined>
  ) =>
    useQuery({
      queryKey: computed(() => [
        'eos',
        'table',
        selectedSite,
        'summary',
        forDate.value?.month,
        forDate.value?.year,
        timezone,
      ]),
      queryFn: () =>
        getTableMonthlySummary({
          site: selectedSite.value!,
          month: forDate.value!.month,
          year: forDate.value!.year,
          timezone: timezone.value!,
        }),
      enabled: computed(() => !!selectedSite.value && forDate.value?.isValid && !!timezone.value),
    })

  const currentEnergyConsumptionQuery = (selectedSite: Ref<string | undefined>) =>
    useQuery({
      queryKey: ['eos', 'table', 'current-energy-consumption', selectedSite],
      queryFn: () => getCurrentSiteEnergyConsumption(selectedSite.value!),
      refetchInterval: 60_000,
      enabled: computed(() => !!selectedSite.value),
      placeholderData: (prev) => prev,
    })

  const tableNBPTQuery = (forDate: Ref<DateTime | undefined>) =>
    useQuery({
      queryKey: ['eos', 'nbpt', forDate],
      queryFn: () => getTableNBPT(forDate.value!.month, forDate.value!.year),
      enabled: computed(() => forDate.value?.isValid),
    })

  const updateSitePowerPositionMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: ({ siteID, form, dateTime }: { siteID: string; form: ISitePowerPositionForm; dateTime: string }) =>
      updateSitePowerPosition(siteID, form),
    onError: NotifyErrorWrapper('Failed to update site power position'),
    onSuccess: (_, { siteID, dateTime }) => {
      queryClient.invalidateQueries({ queryKey: ['eos', 'table', siteID, dateTime] })
      NotifySuccess(`Site power position updated`)
    },
  })

  /**
   * Factory function to define a site DA bid query.
   *
   * @param {string} siteId the site ID.
   * @returns the site DA bid.
   */
  const siteDABidQuery = (siteId: MaybeRefOrGetter<string | undefined>) =>
    useQuery({
      queryKey: ['eos', siteId, 'da', 'bid'],
      queryFn: async () => getSiteDABid(toValue(siteId)!),
      enabled: computed(() => !!toValue(siteId)),
    })

  const siteDABidMutation = useMutation({
    mutationFn: updateSiteDABid,
    onSuccess: (data) => {
      queryClient.setQueryData(['eos', data.site_id, 'da', 'bid'], data)
    },
  })

  return {
    eosTableQuery,
    eosTableMonthlySummaryQuery,
    currentEnergyConsumptionQuery,
    tableNBPTQuery,
    siteDABidQuery,
    siteDABidMutation,
    updateSitePowerPositionMutation,
  }
}
